import { Alert } from "@/src/lib/alert";
import { Text } from "@/src/components/ui/BrandText";
import { border, surface, text as uiText, brand } from "@/src/theme/tokens";
import { router } from "expo-router";
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import React from "react";
import { ActivityIndicator, Pressable, ScrollView, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { auth, db } from "../../src/lib/firebase";
import { friendlyFirebaseError } from "../../src/lib/friendlyFirebaseError";
import { applyModerationAction, type ModerationActionName } from "../../src/lib/moderation";

const BG = surface.secondary;
const CARD = surface.primary;
const BORDER = border.subtle;
const MUTED = uiText.muted;

type CaseItem = {
  id: string;
  targetType?: string;
  targetId?: string;
  reportedUserUid?: string;
  groupId?: string;
  reportCount?: number;
  uniqueReporterCount?: number;
  severity?: string;
  source?: string;
  autoModerationResult?: { decision?: string; reasonCodes?: string[] };
};

export default function ModerationScreen() {
  const [allowed, setAllowed] = React.useState<boolean | null>(null);
  const [items, setItems] = React.useState<CaseItem[]>([]);
  const [busy, setBusy] = React.useState<string | null>(null);

  React.useEffect(() => {
    let active = true;
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        if (active) setAllowed(false);
        return;
      }
      user.getIdTokenResult(true).then((token) => {
        if (active) setAllowed(token.claims.platformModerator === true);
      }).catch(() => active && setAllowed(false));
    });
    return () => { active = false; unsubscribe(); };
  }, []);

  React.useEffect(() => {
    if (allowed === false) router.replace("/(tabs)");
  }, [allowed]);

  React.useEffect(() => {
    if (allowed !== true) return;
    const casesQuery = query(
      collection(db, "moderationCases"),
      where("status", "==", "open"),
      orderBy("updatedAt", "desc"),
      limit(50)
    );
    return onSnapshot(casesQuery, (snap) => {
      setItems(snap.docs.map((item) => ({ id: item.id, ...(item.data() as any) })));
    }, () => setItems([]));
  }, [allowed]);

  async function act(item: CaseItem, action: ModerationActionName, durationDays?: 1 | 7) {
    if (busy) return;
    setBusy(item.id);
    try {
      await applyModerationAction({ caseId: item.id, action, durationDays, internalReason: `Décision modérateur: ${action}` });
    } catch (error: any) {
      Alert.alert("Erreur", friendlyFirebaseError(error, "Action impossible."));
    } finally {
      setBusy(null);
    }
  }

  if (allowed === null) return <SafeAreaView style={{ flex: 1, backgroundColor: BG, alignItems: "center", justifyContent: "center" }}><ActivityIndicator color={uiText.brand} /></SafeAreaView>;
  if (!allowed) return <SafeAreaView style={{ flex: 1, backgroundColor: BG, alignItems: "center", justifyContent: "center", padding: 24 }}><Text style={{ color: uiText.primary, fontWeight: "900" }}>Accès refusé.</Text></SafeAreaView>;

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: BG }} edges={["top", "left", "right"]}>
      <ScrollView contentContainerStyle={{ padding: 16, gap: 12 }}>
        <Pressable onPress={() => router.back()}><Text style={{ color: uiText.brand, fontWeight: "800" }}>‹ Retour</Text></Pressable>
        <Text style={{ color: uiText.primary, fontSize: 28, fontWeight: "900" }}>Modération Neqraw</Text>
        {!items.length ? <Text style={{ color: MUTED }}>Aucun dossier ouvert.</Text> : null}
        {items.map((item) => {
          const result = item.autoModerationResult;
          return (
            <View key={item.id} style={{ backgroundColor: CARD, borderWidth: 1, borderColor: BORDER, borderRadius: 18, padding: 14, gap: 8 }}>
              <Text style={{ color: uiText.primary, fontWeight: "900" }}>{item.targetType ?? "contenu"} · {item.severity ?? "normal"}</Text>
              <Text style={{ color: MUTED }}>Auteur : {item.reportedUserUid ?? "inconnu"}</Text>
              {item.groupId ? <Text style={{ color: MUTED }}>Groupe : {item.groupId}</Text> : null}
              <Text style={{ color: MUTED }}>Signalements : {item.reportCount ?? 0} · reporters : {item.uniqueReporterCount ?? 0}</Text>
              <Text style={{ color: uiText.secondary, opacity: 0.55 }}>Contenu sensible flouté par défaut · {result?.decision ?? item.source ?? "manuel"}</Text>
              {result?.reasonCodes?.length ? <Text style={{ color: brand.accent }}>Codes : {result.reasonCodes.join(", ")}</Text> : null}
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                <Action label="Approuver" onPress={() => act(item, "content_approved")} disabled={busy === item.id} />
                <Action label="Supprimer" onPress={() => act(item, "content_removed")} disabled={busy === item.id} />
                <Action label="24 h" onPress={() => act(item, "posting_restricted", 1)} disabled={busy === item.id} />
                <Action label="7 jours" onPress={() => act(item, "posting_restricted", 7)} disabled={busy === item.id} />
                <Action label="Suspendre" onPress={() => act(item, "temporary_suspension")} disabled={busy === item.id} />
                <Action label="Bannir" onPress={() => act(item, "permanent_ban")} disabled={busy === item.id} />
                <Action label="Classer sans suite" onPress={() => act(item, "no_action")} disabled={busy === item.id} />
              </View>
            </View>
          );
        })}
      </ScrollView>
    </SafeAreaView>
  );
}

function Action({ label, onPress, disabled }: { label: string; onPress: () => void; disabled: boolean }) {
  return <Pressable disabled={disabled} onPress={onPress} style={{ paddingHorizontal: 10, paddingVertical: 8, borderRadius: 999, backgroundColor: surface.selected, opacity: disabled ? 0.5 : 1 }}><Text style={{ color: uiText.primary, fontWeight: "800" }}>{label}</Text></Pressable>;
}
